import React, { createContext, useContext, useEffect, useState } from 'react';
import { User, Session, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/config/supabase';
import { queryClient } from '@/config/query-client';
import {
  mindspaceKeys,
  type MindspaceGraphData,
} from '@/hooks/mindspace/useMindspace';
import { mindspaceApi } from '@/lib/api-client';
import { identifyUser, resetIdentity } from '@/config/observability';
import { validateGraphData } from '@/utils/graphValidation';

/** Social sign-in providers enabled in Supabase. Google only, for now. */
export type OAuthProvider = 'google';

export interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (
    email: string,
    password: string,
    fullName?: string
  ) => Promise<{ data: unknown; error: AuthError | null }>;
  signIn: (
    email: string,
    password: string
  ) => Promise<{ data: unknown; error: AuthError | null }>;
  signInWithOtp: (email: string) => Promise<{ error: AuthError | null }>;
  /**
   * Leave for the provider's consent screen. Sign-in and sign-up in one: the
   * first visit creates the account, and a provider email that matches an
   * existing account is linked to it by Supabase rather than duplicated.
   * Resolves only on failure - on success the browser is already navigating.
   */
  signInWithProvider: (
    provider: OAuthProvider
  ) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
  updatePassword: (password: string) => Promise<{ error: AuthError | null }>;
  resendConfirmation: (email: string) => Promise<{ error: AuthError | null }>;
}

/**
 * Exported so a test/Storybook provider can supply a session without going
 * through Supabase. `AuthProvider` below remains the only production writer —
 * nothing in `src/` outside this file should render `AuthContext.Provider`.
 * See `src/stories/auth.tsx` for the catalog's signed-in stand-in.
 */
export const AuthContext = createContext<AuthContextType | undefined>(
  undefined
);

/**
 * Read the session Supabase persists in localStorage WITHOUT going through
 * `supabase.auth.getSession()`. `getSession()` acquires the GoTrue navigator
 * lock and can stall on a cold boot (or a bfcache restore) - long enough to
 * trip the auth failSafe below, which would otherwise strand a genuinely
 * authenticated user on the login page. This lock-free read lets us recover
 * the user in that window instead of falsely treating them as logged out.
 *
 * Supabase (supabase-js v2) stores the session as JSON under a
 * `sb-<project-ref>-auth-token` key. We scan for that key pattern (rather than
 * reconstructing the ref) and accept either the raw session object or the
 * older `{ currentSession }` / `{ session }` wrappers. Anything malformed or a
 * missing localStorage (SSR / blocked storage) resolves to `null`.
 */
function readPersistedSession(): Session | null {
  if (typeof localStorage === 'undefined') return null;
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !/^sb-.*-auth-token$/.test(key)) continue;

    // Parse each candidate independently: an unrelated malformed key must not
    // abort the scan and lose the real session.
    try {
      const raw = localStorage.getItem(key);
      if (!raw) continue;

      const parsed = JSON.parse(raw);
      const session = parsed?.access_token
        ? parsed
        : (parsed?.currentSession ?? parsed?.session ?? null);

      if (session?.access_token && session?.user) {
        return session as Session;
      }
    } catch {
      // Malformed value under this key - skip it and keep scanning.
    }
  }
  return null;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  // Seed synchronously from the persisted session so a returning user is never
  // treated as logged out (no chance of the failSafe bouncing them to /auth).
  // `getSession()` + `onAuthStateChange` below still reconcile - refreshing the
  // token, or signing out for real if the session is truly dead.
  const persisted = readPersistedSession();

  // Only skip the loading gate when the persisted access token is STILL VALID.
  // If it's expired/near-expiry, keep loading=true so getSession() refreshes it
  // before any protected page mounts and fires token-dependent queries (e.g. the
  // onboarding wizard's /users/me) with a stale token - which would 401 and, on
  // a cold boot, leave the wizard stuck instead of bouncing an onboarded user
  // back. `expires_at` is a Unix timestamp in SECONDS; keep a 10s safety margin.
  const persistedIsFresh =
    !!persisted?.expires_at &&
    persisted.expires_at * 1000 > Date.now() + 10_000;

  const [user, setUser] = useState<User | null>(persisted?.user ?? null);
  const [session, setSession] = useState<Session | null>(persisted);
  const [loading, setLoading] = useState(!persistedIsFresh);

  useEffect(() => {
    let mounted = true;

    // Safety net: never let a wedged auth check hang the whole app behind
    // the ProtectedRoute spinner. If getSession() never settles (a stale
    // Supabase navigator lock, or a token refresh that never comes back),
    // stop blocking after a couple seconds so routing can proceed.
    //
    // Crucially, recover the persisted session here instead of leaving
    // `user` null: a stall must NOT masquerade as a sign-out, or ProtectedRoute
    // would evict an authenticated user to /auth. (It's a no-op when we already
    // seeded from storage above; it matters when getSession() is mid-refresh and
    // storage was empty at first paint.)
    const failSafe = setTimeout(() => {
      if (!mounted) return;
      const recovered = readPersistedSession();
      if (recovered) {
        setSession(recovered);
        setUser(recovered.user ?? null);
      }
      setLoading(false);
    }, 2500);

    // Get initial session. `loading` is cleared in `finally` (below), not
    // inline, so an expired/invalid refresh token - which makes getSession()
    // reject - can never leave us stuck on the spinner.
    supabase.auth
      .getSession()
      .then(({ data: { session } }) => {
        if (!mounted) return;

        setSession(session);
        setUser(session?.user ?? null);

        if (session?.user) {
          identifyUser(session.user.id, session.user.email ?? undefined);
        }

        // Prefetch mindspace data after successful authentication
        if (session?.user) {
          queryClient.prefetchQuery({
            queryKey: mindspaceKeys.graph(),
            queryFn: async () => {
              try {
                const data = await mindspaceApi.getGraph();

                // ✅ Apply validation to filter invalid links
                const rawGraphData = {
                  nodes: data.nodes || [],
                  links: data.links || [],
                };

                const validatedData = validateGraphData(rawGraphData);

                return {
                  ...validatedData,
                  lastUpdated: data.last_updated,
                } as unknown as MindspaceGraphData;
              } catch (error) {
                // Return empty data on error (don't block app)
                return { nodes: [], links: [] } as MindspaceGraphData;
              }
            },
          });
        }
      })
      .catch(() => {
        // getSession rejects on an invalid/expired refresh token. Treat it
        // as signed out rather than hanging the app on the spinner.
        if (!mounted) return;
        setSession(null);
        setUser(null);
      })
      .finally(() => {
        if (!mounted) return;
        clearTimeout(failSafe);
        setLoading(false);
      });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((event, session) => {
      if (!mounted) return;

      setSession(session);
      setUser(session?.user ?? null);
      setLoading(false);

      if (event === 'SIGNED_IN' && session?.user) {
        identifyUser(session.user.id, session.user.email ?? undefined);
      }

      // Handle sign in - prefetch mindspace data
      if (event === 'SIGNED_IN' && session?.user) {
        queryClient.prefetchQuery({
          queryKey: mindspaceKeys.graph(),
          queryFn: async () => {
            try {
              const data = await mindspaceApi.getGraph();

              // ✅ Apply validation to filter invalid links
              const rawGraphData = {
                nodes: data.nodes || [],
                links: data.links || [],
              };

              const validatedData = validateGraphData(rawGraphData);

              return {
                ...validatedData,
                lastUpdated: data.last_updated,
              } as unknown as MindspaceGraphData;
            } catch (error) {
              return { nodes: [], links: [] } as MindspaceGraphData;
            }
          },
        });
      }

      // Handle sign out - clear React Query cache
      if (event === 'SIGNED_OUT') {
        queryClient.clear();
        resetIdentity();
      }
    });

    return () => {
      mounted = false;
      clearTimeout(failSafe);
      subscription.unsubscribe();
    };
  }, []);

  const signUp = async (email: string, password: string, fullName?: string) => {
    try {
      const result = await supabase.auth.signUp({
        email,
        password,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
          data: {
            full_name: fullName || '',
          },
        },
      });
      return result;
    } catch (error) {
      return { data: null, error: error as AuthError };
    }
  };

  /**
   * Re-send the sign-up confirmation email.
   *
   * There was no resend path anywhere in the app before this. If the
   * confirmation landed in spam or expired, the user had no route forward: they
   * could not re-register, because the account already exists, and nothing
   * offered to send another. Every "we emailed you" screen needs this.
   *
   * `type: 'signup'` is the confirmation mail specifically - `supabase.auth
   * .resend` also handles 'email_change' and 'sms', which are not this flow.
   * Supabase rate-limits it server-side, so no client-side throttle here.
   */
  const resendConfirmation = async (email: string) => {
    try {
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      return { error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      const result = await supabase.auth.signInWithPassword({
        email,
        password,
      });
      return result;
    } catch (error) {
      return { data: null, error: error as AuthError };
    }
  };

  const signInWithOtp = async (email: string) => {
    try {
      const result = await supabase.auth.signInWithOtp({
        email,
        options: {
          emailRedirectTo: `${window.location.origin}/auth/callback`,
        },
      });
      return { error: result.error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signInWithProvider = async (provider: OAuthProvider) => {
    try {
      const result = await supabase.auth.signInWithOAuth({
        provider,
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      });
      return { error: result.error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const signOut = async () => {
    try {
      const result = await supabase.auth.signOut();
      return result;
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const resetPassword = async (email: string) => {
    try {
      const result = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/auth/reset-password`,
      });
      return { error: result.error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const updatePassword = async (password: string) => {
    try {
      const result = await supabase.auth.updateUser({ password });
      return { error: result.error };
    } catch (error) {
      return { error: error as AuthError };
    }
  };

  const value = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithOtp,
    signInWithProvider,
    signOut,
    resetPassword,
    updatePassword,
    resendConfirmation,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
